import React from 'react';
import { useQuery } from '@tanstack/react-query';

import type { UseQueryWrapper, AdminOwnerEmployer } from 'app/types';
import { useSnakeBar } from '../../useSnakeBar';
import type { GetOwnerEmployersArgs } from '../../../Domain/Tools/Services';
import { toolsServices } from '../../../Domain/Tools/Services';

type GetOwnerEmployersQueryVariables = GetOwnerEmployersArgs;

type GetOwnerEmployersQueryResultData = {
  data: AdminOwnerEmployer[];
  totalItems: number;
};

const queryFn = (variables: GetOwnerEmployersQueryVariables) => async () => {
  const response = await toolsServices.getOwnerEmployers(variables);
  return response.data;
};

const QUERY_KEY = 'getOwnerEmployers';

export const useOwnerEmployersQuery: UseQueryWrapper<
  GetOwnerEmployersQueryResultData,
  GetOwnerEmployersQueryVariables
> = ({ variables, options }) => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: QUERY_KEY });
  }, [showErrorSnakeBar]);

  const select = React.useCallback((data: any) => {
    return {
      data: data['hydra:member'] || [],
      totalItems: data['hydra:totalItems'] || 0,
    };
  }, []);

  return useQuery<GetOwnerEmployersQueryResultData>(
    [QUERY_KEY, ...Object.values(variables || {})],
    queryFn(variables),
    {
      select,
      onError,
      enabled: !!variables.ownerId,
      ...(options || {}),
    },
  );
};
